<template>
  <v-dialog
    v-model="isDialogOpen"
    max-width="680px"
    content-class="fixed-height-md"
  >
    <modal-card
      v-if="isDialogOpen"
      title="Import Training Files"
      fixed
      :loading="loading"
      @close="closeDialog"
    >
      <template #headerActions>
        <v-btn color="primary" class="appMain--text" @click="openFilePicker">
          <v-icon left small>mdi-plus</v-icon>
          Add Files
        </v-btn>
      </template>

      <image-dropzone
        ref="imageDropzone"
        class="fill-height pa-4"
        :accepted-formats="acceptedFormats"
        :class="{ 'dropzone-loading': uploading }"
        multiple
        @fileDropped="handleFileDrop"
      >
        <div v-if="uploading" class="files-loading-overlay">
          <div class="files-loading-content">
            <v-progress-circular
              indeterminate
              color="primary"
              size="56"
              width="4"
            />
            <h3 class="files-loading-title">Uploading Files</h3>
            <div class="files-loading-stats">
              <div class="files-stat-item">
                <span class="files-stat-number">{{ queueStats.active }}</span>
                <span class="files-stat-label">Uploading</span>
              </div>
              <div class="files-stat-item">
                <span class="files-stat-number">{{ queueStats.waiting }}</span>
                <span class="files-stat-label">Waiting</span>
              </div>
              <div class="files-stat-item">
                <span class="files-stat-number">{{ queueStats.done }}</span>
                <span class="files-stat-label">Completed</span>
              </div>
            </div>
            <div class="files-progress-info">
              <v-progress-linear
                :value="overallProgress"
                height="6"
                rounded
                color="primary"
                background-color="rgba(124, 77, 255, 0.1)"
              />
              <span class="files-progress-text"
                >{{ overallProgress }}% Complete</span
              >
            </div>
          </div>
        </div>
        <!-- Files (saved + in-flight with progress) -->
        <div class="imported-files" v-if="files.length">
          <v-row>
            <!-- Saved files -->
            <v-col
              v-for="file in files"
              :key="'saved-' + file.id"
              cols="12"
              sm="6"
              md="4"
            >
              <file-card :file="file" deleteOnly />
            </v-col>
          </v-row>
        </div>

        <!-- Placeholder -->
        <div class="import-placeholder" v-else @click="openFilePicker">
          <div class="import-placeholder__icon">
            <v-icon size="64" color="primary">mdi-cloud-upload</v-icon>
          </div>
          <h3 class="import-placeholder__title">Drag & Drop Files Here</h3>
          <p class="import-placeholder__subtitle">
            Or click to browse and select multiple files
          </p>
          <p class="import-placeholder__formats">
            Supported formats: {{ formatDisplay }}
          </p>
        </div>
      </image-dropzone>

      <template v-slot:actions>
        <v-spacer />
        <v-btn color="appMain" @click="$emit('finish')"> Finish </v-btn>
      </template>
    </modal-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';

import FilestackLib from '@/lib/filestackLib';

import ModalCard from '@/components/basic/modal-card/ModalCard.vue';
import ImageDropzone from '@/components/basic/image-dropzone/ImageDropzone.vue';
import FileCard from '@/views/files/files/FileCard.vue';

let localIdCounter = 1;

export default {
  name: 'ImportFilesDialog',
  components: {
    ModalCard,
    ImageDropzone,
    FileCard,
  },
  props: {
    value: { type: Boolean, default: false },
  },

  data() {
    return {
      loading: false,
      uploading: false,
      MAX_CONCURRENT: 3,
      activeUploads: [],
      queuedUploads: [],
      finishedUploads: 0,
      acceptedFormats:
        'image/*,.pdf,.xls,.xlsx,.doc,.docx,.txt,.ppt,.pptx,.zip,.rar',
    };
  },

  computed: {
    ...mapState('files', {
      files: 'all',
      pagination: (state) => ({
        count: state.count,
        page: state.page,
        pages: state.pages,
      }),
    }),
    ...mapGetters('users', ['userDefaults', 'hasAchievement']),

    isDialogOpen: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    defaultFolderId() {
      return this.userDefaults?.file_folder_id || null;
    },

    formatDisplay() {
      const formats = this.acceptedFormats.split(',');
      const displayFormats = formats.map((format) => {
        if (format.includes('*')) return format.split('/')[0] + ' files';
        return format.replace('.', '').toUpperCase();
      });
      return displayFormats.join(', ');
    },

    queueStats() {
      const total =
        this.activeUploads.length +
        this.queuedUploads.length +
        this.finishedUploads;
      return {
        total,
        active: this.activeUploads.length,
        waiting: this.queuedUploads.length,
        done: this.finishedUploads,
      };
    },

    overallProgress() {
      const total = this.queueStats.total;
      if (total === 0) return 0;
      return Math.round((this.finishedUploads / total) * 100);
    },
  },

  watch: {
    files(val) {
      if (!val.length) return;
      this.sendAchievement('imported_files');
    },
  },

  methods: {
    ...mapActions('files', { createOne: 'createOne', loadFiles: 'load' }),
    ...mapActions('users', ['sendAchievement']),

    handleFileDrop(files) {
      const fileList = Array.from(files || []);
      if (!fileList.length) return;

      this.queueFiles(fileList);
      this.startUploadProcess();
    },

    queueFiles(files) {
      files.forEach((file) => {
        const localId = localIdCounter++;
        this.queuedUploads.push({
          localId,
          fileRef: file,
          name: file.name,
          size: file.size,
        });
      });
    },

    startUploadProcess() {
      this.uploading = true;
      this.pumpQueue();
    },

    openFilePicker() {
      this.$refs.imageDropzone?.openPicker();
    },

    pumpQueue() {
      while (
        this.activeUploads.length < this.MAX_CONCURRENT &&
        this.queuedUploads.length
      ) {
        const next = this.queuedUploads.shift();
        this.startUpload(next);
      }
      // turn off spinner when nothing is left
      if (!this.activeUploads.length && !this.queuedUploads.length) {
        this.uploading = false;
      }
    },

    async startUpload(item) {
      const entry = this.createUploadEntry(item);
      this.activeUploads.push(entry);

      try {
        const file = await this.performUpload(entry);
        await this.saveFile(file);
        this.finishEntry(entry.localId);
      } catch (error) {
        this.handleUploadError(entry, error);
      }
    },

    createUploadEntry(item) {
      return {
        localId: item.localId,
        fileRef: item.fileRef,
        name: item.name,
        size: item.size,
        progress: 0,
        status: 'uploading',
        statusLabel: 'Uploading…',
        cancel: null,
        error: null,
      };
    },

    async performUpload(entry) {
      const fs = new FilestackLib();

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        if (entry.progress < 90) {
          entry.progress += Math.random() * 15;
        }
      }, 300);

      try {
        const file = await fs.upload(entry.fileRef);
        clearInterval(progressInterval);
        entry.progress = 100;
        entry.status = 'done';
        entry.statusLabel = 'Processing…';
        return file;
      } catch (error) {
        clearInterval(progressInterval);
        throw error;
      }
    },

    handleUploadError(entry, error) {
      // Remove the failed entry from active uploads to prevent loading freeze
      this.activeUploads = this.activeUploads.filter(
        (u) => u.localId !== entry.localId
      );

      this.pumpQueue();
    },

    finishEntry(localId) {
      this.activeUploads = this.activeUploads.filter(
        (u) => u.localId !== localId
      );
      this.finishedUploads += 1;
      this.pumpQueue();
    },

    async saveFile(file) {
      const fileData = {
        url: file.url,
        filename: file.filename,
        mimetype: file.mimetype,
        size: file.size,
        handle: file.handle,
        container: file.container,
        key: file.key,
        path: file.path,
        status: file.status,
        type: 'file',
        folder_id: this.defaultFolderId,
      };

      await this.createOne(fileData);
    },

    async loadFilesChunk() {
      const { page, pages } = this.pagination;
      if (page >= pages && pages > 1) return;

      const nextPage = pages > 1 ? +page + 1 : 1;

      try {
        this.loading = true;

        await this.loadFiles({
          params: {
            folder_id: this.defaultFolderId,
            page: nextPage,
            per_page: 500,
            query: this.search,
          },
        });
      } finally {
        this.loading = false;
      }
    },

    closeDialog() {
      this.isDialogOpen = false;
    },
  },
  mounted() {
    this.loadFilesChunk();
  },
};
</script>

<style lang="less">
.files-loading-overlay {
  position: absolute;
  top: -16px;
  left: -16px;
  right: -16px;
  bottom: -16px;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100% + 32px);
  width: calc(100% + 32px);

  .files-loading-content {
    text-align: center;
    max-width: 320px;
    padding: 20px;

    .files-loading-title {
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--v-primary-base);
      margin: 16px 0 24px 0;
    }

    .files-loading-stats {
      display: flex;
      justify-content: center;
      gap: 48px;
      margin-bottom: 32px;

      .files-stat-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;

        &:not(:last-child)::after {
          content: '';
          position: absolute;
          right: -24px;
          top: 50%;
          transform: translateY(-50%);
          width: 1px;
          height: 24px;
          background: rgba(124, 77, 255, 0.2);
        }

        .files-stat-number {
          font-size: 2rem;
          font-weight: 600;
          color: var(--v-primary-base);
          line-height: 1;
          margin-bottom: 6px;
        }

        .files-stat-label {
          font-size: 0.75rem;
          color: var(--v-inactive-base);
          font-weight: 400;
          text-align: center;
          opacity: 0.8;
        }
      }
    }

    .files-progress-info {
      width: 100%;

      .files-progress-text {
        font-size: 0.875rem;
        color: var(--v-inactive-base);
        margin-top: 8px;
        display: block;
        text-align: center;
      }
    }
  }
}

// File Grid
.imported-files {
  position: relative;
  z-index: 1;
}

// Upload Placeholder
.import-placeholder {
  text-align: center;
  padding: 40px 20px;
  height: 100%;
  border: 2px dashed var(--v-primary-base);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  cursor: pointer;
  opacity: 0.9;

  &:hover {
    opacity: 1;
  }

  &__icon {
    margin-bottom: 16px;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: var(--v-primary-base);
  }

  &__subtitle {
    font-size: 1rem;
    color: var(--v-inactive-base);
    margin-bottom: 16px;
  }

  &__formats {
    font-size: 0.875rem;
    color: var(--v-inactive-base);
    opacity: 0.8;
  }
}

// Dropzone Loading State
.dropzone-loading {
  .image-dropzone__content {
    overflow: hidden;
  }
}
</style>
